import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Activity } from '../../models/activity';
import { ActivityExtra } from '../../models/activity-extra';
import { ActivityPlaceSelectionType } from '../../models/activity-place-selection-type.enum';
import { BundleItem } from '../../models/bundle-item';
import { BundleItemExtra } from '../../models/bundle-item-extra';
import { BundleItemStartTime } from '../../models/bundle-item-start-time';
import { BundleItemStartTimeCondition } from '../../models/bundle-item-start-time-condition';
import { BundlePlaceSelectionType } from '../../models/bundle-place-selection-type.enum';
import { ExtraConfig } from '../../models/extra-config';
import { ExtraSelectionType } from '../../models/extra-selection-type.enum';
import { GetActivityRequest } from '../../models/get-activity-request';
import { PricingCategory } from '../../models/pricing-category';
import { Rate } from '../../models/rate';
import { StartTime } from '../../models/start-time';
import { InvSysDataService } from '../../services/inv-sys-data.service';
import { InventorySystem } from 'src/app/shared/models/inventory-system.enum';
import { formatMinutes, MINUTES_PER_DAY, parseTimeToMinutes } from '../../utils/start-time.utils';

function customStartTimeValidator(): ValidatorFn {
  return (bundleItemForm: UntypedFormGroup) => {
    const customizedCtrl = bundleItemForm.get('customized');
    const startingTimesCtrl = bundleItemForm.get('startingTimes');

    if (!customizedCtrl || !startingTimesCtrl) {
      return null;
    }

    const customizedValue = customizedCtrl.value;

    if (!customizedValue) {
      return null;
    }

    const startingTimesValue = startingTimesCtrl.value as BundleItemStartTime[];

    if (!startingTimesValue) {
      return null;
    }

    if (!startingTimesValue.length) {
      startingTimesCtrl.setErrors({ required: true });
    }

    if (startingTimesValue.length === 1) {
      startingTimesCtrl.setErrors(null);
    }

    if (customizedValue && startingTimesValue.length > 1) {
      startingTimesCtrl.setErrors({ noMoreThanOneStartTimeWhenCustom: true });
      return { noMoreThanOneStartTimeWhenCustom: true };
    }

    return null;
  };
}

@Component({
  selector: 'bundle-item',
  templateUrl: './bundle-item.component.html',
  styleUrls: ['./bundle-item.component.scss']
})
export class BundleItemComponent implements OnInit {
  @ViewChild(MatExpansionPanel) private matExpansionPanel: MatExpansionPanel;
  @Input() itemForm: UntypedFormGroup;
  @Input() disabled: boolean;
  @Input() bundlePricingCategories: PricingCategory[];
  @Input() bundleDiscount: number;
  // Occupied-until minute-of-day of the preceding tours on this day. Earlier start times (any item, Bokun or Liva)
  // are flagged with a warning class + tooltip but stay selectable — the manager decides.
  @Input() precedingBarrierMinutes: number | null = null;
  // Conditional-start-times mode: later items map their start times per the first item's start time.
  @Input() conditionalStartTimes = false;
  @Input() isFirstInDay = false;
  // The first item's selected start times on this day (trigger options) and its occupied window (duration + travel).
  @Input() firstItemStartTimes: string[] = [];
  @Input() firstItemOccupiedMinutes = 0;
  startTimeBeforePreviousTourTooltip = 'Overlaps the previous tour on this day (its time plus travel and check-in). You can still select it if the schedule is intentional.';
  candidateBeforeTriggerTooltip = 'Overlaps the first tour (its time plus travel and check-in). You can still select it if the schedule is intentional.';

  // TODO: move magic strings to a separate resource
  dataNotLoadedTooltip = 'To edit this bundle item fully please load data from inventory system by pressing the cloud icon';
  notAssignedToRateToolTip = 'Not assigned to rate. Check inventory system configuration';
  occupancyNotSupportedTooltip = 'Occupancy above 1 currently not supported';
  extraSelectionTypeTooltip = 'Note that preselected extras in inventory system will be added';
  pickupDropoffSelectionTypeEnum = BundlePlaceSelectionType;
  extraSelectionTypeEnum = ExtraSelectionType;

  get formValue(): BundleItem {
    return this.itemForm.getRawValue();
  }

  /** A Liva product cannot carry a discount — its discount is always 0% and the input is disabled. */
  get isLivaItem(): boolean {
    return this.formValue?.invSys === InventorySystem.Liva;
  }

  get idInInvSys(): number {
    return this.itemForm.get('idInInvSys').value;
  }

  get hasActivity(): boolean {
    return this.invSysDataService.loadedData.has(this.idInInvSys);
  }

  get activity(): Activity {
    return this.invSysDataService.loadedData.get(this.idInInvSys);
  }

  get isActivityLoading(): boolean {
    return this.invSysDataService.isLoading.get(this.idInInvSys);
  }

  get customized(): UntypedFormControl {
    return this.itemForm.get('customized') as UntypedFormControl;
  }

  get customizedTime(): UntypedFormControl {
    return this.itemForm.get('customizedTime') as UntypedFormControl;
  }

  get durationMinutes(): UntypedFormControl {
    return this.itemForm.get('durationMinutes') as UntypedFormControl;
  }

  get travelTimeMinutes(): UntypedFormControl {
    return this.itemForm.get('travelTimeMinutes') as UntypedFormControl;
  }

  get checkInMinutes(): UntypedFormControl {
    return this.itemForm.get('checkInMinutes') as UntypedFormControl;
  }

  get active(): UntypedFormControl {
    return this.itemForm.get('active') as UntypedFormControl;
  }

  get startingTimes(): UntypedFormControl {
    return this.itemForm.get('startingTimes') as UntypedFormControl;
  }

  get startTimeConditions(): UntypedFormControl {
    return this.itemForm.get('startTimeConditions') as UntypedFormControl;
  }

  /** Whether the conditional per-first-time mapping UI applies to this item (on, and not the day's first item). */
  get showConditionalStartTimes(): boolean {
    return this.conditionalStartTimes && !this.isFirstInDay;
  }

  get pricingCategories(): UntypedFormArray {
    return this.itemForm.get('pricingCategories') as UntypedFormArray;
  }

  get pickupSelectionType(): UntypedFormControl {
    return this.itemForm.get('pickupSelectionType') as UntypedFormControl;
  }

  get pickupPlaceIdInInvSys(): UntypedFormControl {
    return this.itemForm.get('pickupPlaceIdInInvSys') as UntypedFormControl;
  }

  get pickupPlaceTitleInInvSys(): UntypedFormControl {
    return this.itemForm.get('pickupPlaceTitleInInvSys') as UntypedFormControl;
  }

  get customPickupPlaceDescription(): UntypedFormControl {
    return this.itemForm.get('customPickupPlaceDescription') as UntypedFormControl;
  }

  get dropoffSelectionType(): UntypedFormControl {
    return this.itemForm.get('dropoffSelectionType') as UntypedFormControl;
  }

  get dropoffPlaceIdInInvSys(): UntypedFormControl {
    return this.itemForm.controls.dropoffPlaceIdInInvSys as UntypedFormControl;
  }

  get dropoffPlaceTitleInInvSys(): UntypedFormControl {
    return this.itemForm.controls.dropoffPlaceTitleInInvSys as UntypedFormControl;
  }

  get customDropoffPlaceDescription(): UntypedFormControl {
    return this.itemForm.controls.customDropoffPlaceDescription as UntypedFormControl;
  }

  get extras(): UntypedFormControl {
    return this.itemForm.controls.extras as UntypedFormControl;
  }

  get extraSelectionType(): UntypedFormControl {
    return this.itemForm.controls.extraSelectionType as UntypedFormControl;
  }

  constructor(
    private invSysDataService: InvSysDataService,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.customizedTime.disable();
    // Older bundles loaded from the API may not carry the conditions control; ensure it exists.
    if (!this.itemForm.get('startTimeConditions')) {
      this.itemForm.addControl('startTimeConditions', this.fb.control([]));
    }
    // Guard the scheduling fields against negatives: they sum into the occupied window that drives the overlap
    // warning, and a negative travel time would move the barrier earlier and silently stop the warning from firing.
    // (The server mirrors this in ValidateBundle so a direct API call can't bypass the UI.)
    ['durationMinutes', 'travelTimeMinutes', 'checkInMinutes'].forEach(name => {
      const control = this.itemForm.get(name);
      if (control) {
        control.addValidators(Validators.min(0));
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
    this.itemForm.setValidators(customStartTimeValidator());
    this.itemForm.updateValueAndValidity();
  }

  onActiveClick(event: Event): void {
    event.stopPropagation();
  }

  onActiveChange(isChecked: boolean): void {
    this.active.setValue(isChecked);
  }

  onLoadItemData(): void {
    const request = { id: this.formValue.idInInvSys, invSys: this.formValue.invSys, type: this.formValue.type } as GetActivityRequest;

    this.invSysDataService.loadActivity(request).subscribe(() => {
      this.matExpansionPanel.open();
      this.customizedTime.enable();
      this.itemForm.markAllAsTouched();
      this.deselectIncorrectBundleItemFields();
      // Prefill the tour duration from the inventory system (e.g. Bokun) when it hasn't been set yet.
      if (this.durationMinutes && (this.durationMinutes.value === null || this.durationMinutes.value === '') && this.activity?.durationMinutes) {
        this.durationMinutes.setValue(this.activity.durationMinutes);
      }
      // Re-run validators (incl. the parent day-level sequential start-time check) now that the activity's
      // duration is available, so the blocked window reflects the loaded tour length.
      this.itemForm.updateValueAndValidity();
    });
  }

  compareStartTimeOptionWithSelection(option: any, selection: BundleItemStartTime): boolean {
    // tslint:disable-next-line: triple-equals
    return option.id == selection.startTimeIdInInvSys || option.startTimeIdInInvSys == selection.startTimeIdInInvSys;
  }

  startTimeSelectSortComparator(): number {
    return 1;
  }

  isStartTimeAssignedToRate(startTimeIdInInvSys: number): boolean {
    const startTimeIdsAssignedToRate: number[] = [].concat(...this.activity.rates.map(i => i.startTimeIds));

    return startTimeIdsAssignedToRate.some(i => i === startTimeIdInInvSys);
  }

  /**
   * A start time that falls on/before the previous tour's occupied window is flagged (but still selectable): the
   * manager is warned it overlaps the earlier tours on its day and decides whether to keep it. Applies to any item
   * (Bokun or Liva) — the scheduling fields it depends on are not Liva-specific — matching the conditional path.
   */
  isStartTimeBeforePreviousTour(startTime: string): boolean {
    // In conditional mode the union select isn't constrained by a single barrier (each first-tour start time has
    // its own); the per-trigger condition selects enforce the barrier instead. A barrier at/after midnight is
    // suppressed: next-day continuations aren't supported, so it must not flag every same-day option.
    if (this.conditionalStartTimes || this.precedingBarrierMinutes == null || this.precedingBarrierMinutes >= MINUTES_PER_DAY) {
      return false;
    }
    // The barrier already includes each preceding tour's end + its travel-to-next + check-in-before-next.
    const minStart = this.precedingBarrierMinutes;
    const minutes = parseTimeToMinutes(startTime);
    return minutes !== null && minutes < minStart;
  }

  /** The candidate start times (local "HH:mm") currently mapped to a given first-tour start time. */
  getConditionCandidates(trigger: string): string[] {
    const conditions = (this.startTimeConditions?.value || []) as BundleItemStartTimeCondition[];
    return conditions.filter(c => c.triggerStartTime === trigger).map(c => c.candidateStartTime);
  }

  /** Rebuilds this item's condition rows for a first-tour start time from the selected candidate times. */
  onConditionCandidatesChange(trigger: string, candidateTimes: string[]): void {
    const others = ((this.startTimeConditions?.value || []) as BundleItemStartTimeCondition[])
      .filter(c => c.triggerStartTime !== trigger);
    const added = candidateTimes.map(candidateStartTime => ({ triggerStartTime: trigger, candidateStartTime }));
    // Dedupe by (trigger, candidate) — there is no unique constraint on the row, so a duplicate would reach WidgetApi
    // twice and show a duplicated start-time option. Keep a stable (trigger, candidate) order so re-editing a mapping
    // doesn't churn the array order (false "unsaved changes" prompt / reordered PUT payload).
    const combined = [...others, ...added];
    const deduped = combined.filter((c, i) => combined
      .findIndex(o => o.triggerStartTime === c.triggerStartTime && o.candidateStartTime === c.candidateStartTime) === i);
    const sorted = deduped.sort((a, b) =>
      (a.triggerStartTime || '').localeCompare(b.triggerStartTime || '') ||
      (a.candidateStartTime || '').localeCompare(b.candidateStartTime || ''));
    this.startTimeConditions.setValue(sorted);
    this.startTimeConditions.markAsTouched();
    // Re-run the parent day-level conditional validator now that the mapping changed.
    this.itemForm.updateValueAndValidity();
  }

  /**
   * A candidate start time that falls before the first tour's occupied window for a given trigger is flagged:
   * minStart = trigger + firstItem.duration + firstItem.travelTime + firstItem.check-in-before-next.
   */
  isCandidateBeforeTrigger(trigger: string, candidateTime: string): boolean {
    const triggerMinutes = parseTimeToMinutes(trigger);
    const candidateMinutes = parseTimeToMinutes(candidateTime);
    if (triggerMinutes === null || candidateMinutes === null) {
      return false;
    }
    // firstItemOccupiedMinutes already includes the first tour's check-in before the next departure.
    const minStart = triggerMinutes + (Number(this.firstItemOccupiedMinutes) || 0);
    // A barrier at/after midnight would flag every same-day candidate; next-day continuations aren't supported, so
    // suppress the ⚠ in lock-step with getRecommendedStartTime (which returns null in the same case).
    if (minStart >= MINUTES_PER_DAY) {
      return false;
    }
    return candidateMinutes < minStart;
  }

  /**
   * The recommended earliest start time ("HH:mm") for this tour when the first tour starts at the given trigger:
   * trigger + the first tour's occupied window (its duration + travel to next + check-in before the next tour).
   * Returns null when the trigger can't be parsed, or when the window crosses midnight (next-day continuations
   * aren't supported — formatMinutes would wrap and show a misleading same-day time).
   */
  getRecommendedStartTime(trigger: string): string | null {
    const triggerMinutes = parseTimeToMinutes(trigger);
    if (triggerMinutes === null) {
      return null;
    }
    const minStart = triggerMinutes + (Number(this.firstItemOccupiedMinutes) || 0);
    if (minStart >= MINUTES_PER_DAY) {
      return null;
    }
    return formatMinutes(minStart);
  }

  onStartTimeChange(control: UntypedFormControl, event: MatOptionSelectionChange): void {
    if (event.isUserInput) {
      const { value: startTime, selected } = event.source as { value: StartTime, selected: boolean; };

      if (selected) {
        const { rates } = this.activity;
        const ratesWithStartTimes = rates.filter(i => i.startTimeIds.some(id => id === startTime.id));

        const bundleStartTime = {
          startTimeIdInInvSys: startTime.id,
          startTimeInInvSys: startTime.startTime,
          rateIdInInvSys: ratesWithStartTimes[0].id,
          rateTitleInInvSys: ratesWithStartTimes[0].title
        };
        const newValue = [...control.value, bundleStartTime];

        control.setValue(newValue);
      }
      else {
        const newValue = this.startingTimes.value.filter((i: BundleItemStartTime) => i.startTimeIdInInvSys !== startTime.id);
        control.setValue(newValue);
      }
    }
  }

  onCustomizedChange(checked: boolean): void {
    if (checked) {
      const startTimesValue = this.startingTimes.value as BundleItemStartTime[];

      if (startTimesValue && startTimesValue.length > 1) {
        this.startingTimes.setValue([]);
        this.startingTimes.markAllAsTouched();
      }
      if (!this.customizedTime) {
        const customizedStartTimeCtrl: UntypedFormControl = this.fb.control('', Validators.required);

        this.itemForm.addControl('customizedTime', customizedStartTimeCtrl);
      }
      else this.customizedTime.enable();
    }
    else
      this.itemForm.removeControl('customizedTime');
  }

  getBundleItemPricingCategoryRateOptions(startTimeIdInInvSys: number): Rate[] {
    if (!this.activity)
      return [];

    return this.activity.rates.filter(i => i.startTimeIds.some(id => id === startTimeIdInInvSys));
  }

  compareRateOptionToSelection(option: Rate, selection: BundleItemStartTime): boolean {
    // tslint:disable-next-line: triple-equals
    return option.id == selection.rateIdInInvSys;
  }

  onRateChange(rate: Rate, time: BundleItemStartTime): void {
    const newTime = {
      ...time,
      rateTitleInInvSys: rate.title,
      rateIdInInvSys: rate.id
    };
    const newStartingTimes: BundleItemStartTime[] = [...this.startingTimes.value];

    const timeIndex = this.startingTimes.value.findIndex((i: BundleItemStartTime) => i.startTimeIdInInvSys === time.startTimeIdInInvSys);

    newStartingTimes.splice(timeIndex, 1, newTime);

    this.startingTimes.setValue(newStartingTimes);
  }

  getBundleItemPricingCategoryLabel(id: number): string {
    const bundlePricingCategory = this.bundlePricingCategories.find(i => i.id === id);

    if (!bundlePricingCategory)
      return '';

    return bundlePricingCategory.title;
  }

  comparePricingCategoryOptionToSelection(option: any, selection: number): boolean {
    // tslint:disable-next-line: triple-equals
    return option?.pricingCategoryIdInInvSys ? option.pricingCategoryIdInInvSys == selection : option == selection;
  }

  onPricingCategoryChange(category: UntypedFormGroup, value: number): void {
    const foundCategory = this.activity.pricingCategories.find(i => i.id === value);

    const titleControl = category.get('pricingCategoryTitleInInvSys');
    titleControl.patchValue(foundCategory.title);
  }

  isPickupPlaceSelectionTypeDisabled(selectionType: BundlePlaceSelectionType): boolean {
    if (selectionType !== BundlePlaceSelectionType.Independent) {
      if (!this.activity)
        return true;

      const selectedRateIds: number[] = this.startingTimes.value.map((i: BundleItemStartTime) => i.rateIdInInvSys);
      // tslint:disable-next-line: triple-equals
      const selectedRates: Rate[] = this.activity.rates.filter(i => selectedRateIds.some(id => id == i.id));

      return !selectedRates.length || selectedRates.some(i => i.pickupSelectionType === ActivityPlaceSelectionType.Unavailable);
    }

    return false;
  }

  onPickupSelectionTypeChange(selectionType: BundlePlaceSelectionType): void {
    if (selectionType !== BundlePlaceSelectionType.Fixed) {
      this.pickupPlaceIdInInvSys.reset();
      this.pickupPlaceIdInInvSys.setErrors(null);
      this.pickupPlaceTitleInInvSys.reset();
      this.pickupPlaceTitleInInvSys.setErrors(null);
      this.customPickupPlaceDescription.reset();
      this.customPickupPlaceDescription.setErrors(null);
    }
  }

  onFixedPickupChange(placeId: number): void {
    if (placeId !== -2) {
      this.customPickupPlaceDescription.reset();
      this.customPickupPlaceDescription.setErrors(null);
    }

    const foundPlace = this.activity?.places?.pickupPlaces?.find(i => i.id === placeId);

    if (foundPlace)
      this.pickupPlaceTitleInInvSys.setValue(foundPlace.title);
  }

  isPickupPlaceOptionDisabled(placeId: number): boolean {
    if (!this.activity)
      return true;

    if (placeId === -1) {
      const selectedRateIds: number[] = this.startingTimes.value.map((i: BundleItemStartTime) => i.rateIdInInvSys);
      // tslint:disable-next-line: triple-equals
      const selectedRates: Rate[] = this.activity.rates.filter(i => selectedRateIds.some(id => id == i.id));

      return !selectedRates.length || selectedRates.some(i => i.pickupSelectionType === ActivityPlaceSelectionType.Preselected);
    }

    return false;
  }

  isDropoffPlaceSelectionTypeDisabled(selectionType: BundlePlaceSelectionType): boolean {
    if (selectionType !== BundlePlaceSelectionType.Independent) {
      if (!this.activity)
        return true;

      const selectedRateIds: number[] = this.startingTimes.value.map((i: BundleItemStartTime) => i.rateIdInInvSys);
      // tslint:disable-next-line: triple-equals
      const selectedRates: Rate[] = this.activity.rates.filter(i => selectedRateIds.some(id => id == i.id));

      return !selectedRates.length || selectedRates.some(i => i.dropoffSelectionType === ActivityPlaceSelectionType.Unavailable);
    }

    return false;
  }

  onDropoffSelectionTypeChange(selectionType: BundlePlaceSelectionType): void {
    if (selectionType !== BundlePlaceSelectionType.Fixed) {
      this.dropoffPlaceIdInInvSys.reset();
      this.dropoffPlaceIdInInvSys.setErrors(null);
      this.dropoffPlaceTitleInInvSys.reset();
      this.dropoffPlaceTitleInInvSys.setErrors(null);
      this.customDropoffPlaceDescription.reset();
      this.customDropoffPlaceDescription.setErrors(null);
    }
  }

  onFixedDropoffChange(placeId: number): void {
    if (placeId !== -2) {
      this.customDropoffPlaceDescription.reset();
      this.customDropoffPlaceDescription.setErrors(null);
    }

    const foundPlace = this.activity?.places?.dropoffPlaces?.find(i => i.id === placeId);

    if (foundPlace) {
      this.dropoffPlaceTitleInInvSys.setValue(foundPlace.title);
    }
  }

  isDropoffPlaceOptionDisabled(placeId: number): boolean {
    if (!this.activity)
      return true;

    if (placeId === -1) {
      const selectedRateIds: number[] = this.startingTimes.value.map((i: BundleItemStartTime) => i.rateIdInInvSys);
      // tslint:disable-next-line: triple-equals
      const selectedRates: Rate[] = this.activity.rates.filter(i => selectedRateIds.some(id => id == i.id));

      return !selectedRates.length || selectedRates.some(i => i.dropoffSelectionType === ActivityPlaceSelectionType.Preselected);
    }

    return false;
  }

  comparePlaceOptionToSelection(option: number, selection: number): boolean {
    // tslint:disable-next-line: triple-equals
    return option == selection;
  }

  onExtraSelectionTypeChange(selectionType: ExtraSelectionType): void {
    if (selectionType !== ExtraSelectionType.Selected) {
      this.extras.reset([]);
      this.extras.setErrors(null);
    }
  }

  isExtraSelectionTypeOptionDisabled(selectionType: ExtraSelectionType): boolean {
    if (selectionType === ExtraSelectionType.Selected)
      return !this.activity;

    return false;
  }

  isExtraOptionDisabled(extraIdInInvSys: number): boolean {
    if (!this.activity)
      return true;

    const selectedRateIds: number[] = this.startingTimes.value.map((i: BundleItemStartTime) => i.rateIdInInvSys);
    // tslint:disable-next-line: triple-equals
    const selectedRates: Rate[] = this.activity.rates.filter(i => selectedRateIds.some(id => id == i.id));
    const selectedExtraConfigs: ExtraConfig[] = [].concat(...selectedRates.map(i => i.extraConfigs));

    return !selectedExtraConfigs.some(i => i.activityExtraId === extraIdInInvSys);
  }

  compareExtraOptionToSelection(option: any, selection: BundleItemExtra): boolean {
    // tslint:disable-next-line: triple-equals
    return option.extraIdInInvSys === selection.extraIdInInvSys || option.id == selection.extraIdInInvSys;
  }

  onExtrasChange(activityExtras: ActivityExtra[]): void {
    const newValue: BundleItemExtra[] = activityExtras.map(i => ({
      extraIdInInvSys: i.id,
      extraTitleInInvSys: i.title
    }));

    this.extras.setValue(newValue);
  }

  private deselectIncorrectBundleItemFields(): void {
    const currentStartTimes: BundleItemStartTime[] = this.startingTimes.value;
    const filteredStartTimes = currentStartTimes.filter((i) => this.isStartTimeAssignedToRate(i.startTimeIdInInvSys));

    if (currentStartTimes.length > filteredStartTimes.length) {
      this.startingTimes.setValue(filteredStartTimes);
      this.showWarningSnack(`Some start times have been de-selected due to inventory system configuration change. Please review this bundle and press save.`);
    }
  }

  private showWarningSnack(message: string): void {
    this.snackBar.open(
      message,
      'Close',
      { duration: 5000, panelClass: ['warning-snack'] }
    );
  }
}
